
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import { InfoIcon, TrendingUp, TrendingDown, ClipboardList, ExternalLink, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { TradeData } from './types';
import { formatCurrency } from '@/lib/utils';

interface RecentTradesTableProps {
  trades: TradeData[];
  loading: boolean;
}

export const RecentTradesTable: React.FC<RecentTradesTableProps> = ({
  trades = [],
  loading = false
}) => {
  const navigate = useNavigate();

  const viewAllTrades = () => {
    navigate('/trading-history'); // Adjust path if needed
  };

  const getStatusBadge = (status: TradeData['status']) => {
    switch (status) {
        case 'filled': return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3 mr-1"/>Filled
        </div>;
        case 'pending': return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
          <Clock className="h-3 w-3 mr-1"/>Pending
        </div>;
        case 'failed': return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
          <AlertCircle className="h-3 w-3 mr-1"/>Failed
        </div>;
        default: return null; // Or a default badge
    }
  };

  const renderLoadingState = () => (
     <Card>
        <CardHeader>
            <Skeleton className="h-6 w-1/2 mb-1" />
            <Skeleton className="h-4 w-3/4" />
        </CardHeader>
        <CardContent className="h-64 space-y-2">
           {[...Array(5)].map((_, i) => (
             <Skeleton key={i} className="h-10 w-full" />
           ))}
        </CardContent>
     </Card>
  );

 const renderEmptyState = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Recent Trades</CardTitle>
        <CardDescription>Your latest trading activity.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center h-48 text-center border border-dashed rounded-lg bg-gray-50">
            <ClipboardList className="h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-500">No Recent Trades</h3>
            <p className="text-sm text-gray-400 max-w-xs mt-1">
              Executed trades from your active strategies will appear here.
            </p>
        </div>
      </CardContent>
    </Card>
 );


  if (loading) {
    return renderLoadingState();
  }

  if (!loading && trades.length === 0) {
     return renderEmptyState();
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium">Recent Trades</CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="w-64">Shows your most recent trading activity.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {trades.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={viewAllTrades}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
            >
              View All
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
         <CardDescription>Your latest trading activity.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto -mx-6 px-6"> {/* Allow horizontal scroll on small screens */}
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="py-2 px-2 text-left font-medium text-gray-500">Symbol</th>
                  <th className="py-2 px-2 text-left font-medium text-gray-500">Action</th>
                  <th className="py-2 px-2 text-left font-medium text-gray-500">Price</th>
                  <th className="py-2 px-2 text-left font-medium text-gray-500">Quantity</th>
                  <th className="py-2 px-2 text-left font-medium text-gray-500">Status</th>
                  <th className="py-2 px-2 text-right font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade) => (
                  <tr key={trade.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">{trade.symbol}</td>
                    <td className="py-3 px-2">
                       <div
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs w-fit ${
                          trade.action === 'buy'
                            ? 'text-green-600 border border-green-200 bg-green-50'
                            : 'text-red-600 border border-red-200 bg-red-50'
                        }`}
                      >
                        {trade.action === 'buy' ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        <span className="capitalize">{trade.action}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2">{formatCurrency(trade.price)}</td>
                    <td className="py-3 px-2">{trade.quantity}</td>
                     <td className="py-3 px-2">
                        {getStatusBadge(trade.status)}
                     </td>
                    <td className="py-3 px-2 text-right text-gray-500">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="cursor-help underline decoration-dotted">
                            {formatDistanceToNow(new Date(trade.timestamp), { addSuffix: true })}
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{new Date(trade.timestamp).toLocaleString()}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        </div>
      </CardContent>
    </Card>
  );
};
